<template>
    <section class="request-lifecycle" aria-label="Жизненный цикл обращения">
        <div class="request-lifecycle__header">
            <span class="request-lifecycle__heading">Этапы обращения</span>
            <button
                type="button"
                class="request-lifecycle__toggle"
                :aria-expanded="expanded.toString()"
                :aria-controls="detailsId"
                @click="expanded = !expanded"
            >
                {{ expanded ? 'Свернуть' : 'Подробнее' }}
                <span class="request-lifecycle__chevron" aria-hidden="true">{{ expanded ? '⌃' : '⌄' }}</span>
            </button>
        </div>

        <div class="request-lifecycle__steps" aria-label="Этапы">
            <div
                class="request-lifecycle__step request-lifecycle__step_done"
                :class="{ 'request-lifecycle__step_link-done': processingReached }"
            >
                <span class="request-lifecycle__marker" aria-hidden="true">✓</span>
                <span class="request-lifecycle__label">Создано</span>
            </div>
            <div
                class="request-lifecycle__step"
                :class="{
                    'request-lifecycle__step_done': processingDone,
                    'request-lifecycle__step_active': state.active === 'processing',
                    'request-lifecycle__step_link-done': state.solutionReached,
                }"
            >
                <span class="request-lifecycle__marker" aria-hidden="true">{{ processingDone ? '✓' : '' }}</span>
                <span class="request-lifecycle__label">В обработке</span>
            </div>
            <div
                class="request-lifecycle__step"
                :class="{
                    'request-lifecycle__step_done': state.solutionReached && state.closed,
                    'request-lifecycle__step_active': state.active === 'solution',
                    'request-lifecycle__step_link-done': state.solutionReached && state.closed,
                }"
            >
                <span class="request-lifecycle__marker" aria-hidden="true">{{
                    state.solutionReached && state.closed ? '✓' : ''
                }}</span>
                <span class="request-lifecycle__label">Решение</span>
            </div>
            <div class="request-lifecycle__step" :class="{ 'request-lifecycle__step_active': state.closed }">
                <span class="request-lifecycle__marker" aria-hidden="true"></span>
                <span class="request-lifecycle__label">Закрыто</span>
            </div>
        </div>

        <div class="request-lifecycle__summary">
            <span class="request-lifecycle__summary-icon" aria-hidden="true">i</span>
            <span v-if="request.service">{{ supportTitle }} <span aria-hidden="true">·</span></span>
            <span>{{ currentStatusTitle }}</span>
        </div>

        <div v-if="expanded" :id="detailsId" class="request-lifecycle__details">
            <div class="request-lifecycle__current">
                <strong v-if="request.service">{{ supportTitle }}</strong>
                <span>Ответственная группа: {{ groupTitle }}</span>
            </div>
            <p v-if="state.historyPartial" class="request-lifecycle__unknown">Ранний маршрут неизвестен</p>
            <ol v-if="events.length" class="request-lifecycle__events">
                <li v-for="event in events" :key="event.id">
                    <span>{{ event.title }}</span>
                    <time v-if="event.createdAt" :datetime="event.createdAt">{{ $_toLocalDate(event.createdAt) }}</time>
                </li>
            </ol>
            <p v-else class="request-lifecycle__unknown">События истории отсутствуют</p>
        </div>
    </section>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Status from '@/js/enums/RequestStatusType';
import { lifecycleEvents, lifecycleState } from '@/js/requestLifecycle';
import { supportLevelTitle } from '@/js/serviceSupportLevel';

const Workspace = namespace('workspace');

@Component
class RequestLifecycle extends Vue {
    @Prop({ type: Object, required: true }) request;
    @Workspace.State securityGroups;

    expanded = false;

    get detailsId() {
        return `request-lifecycle-details-${this.request.id}`;
    }

    get state() {
        return lifecycleState(this.request);
    }

    get events() {
        return lifecycleEvents(this.request.history, this.securityGroups);
    }

    get supportTitle() {
        return supportLevelTitle(this.request.service?.supportLevel);
    }

    get processingReached() {
        return this.state.active === 'processing' || this.state.solutionReached;
    }

    get processingDone() {
        return this.state.solutionReached;
    }

    get currentStatusTitle() {
        if (this.request.status === Status.CLOSED) return 'Закрыто';
        if (this.state.active === 'solution') return this.state.solutionTitle;
        if (this.request.status === Status.WORKUP) return 'В работе';
        return this.state.processingTitle;
    }

    get groupTitle() {
        return this.request.securityGroup?.name || 'не указана';
    }
}

export default RequestLifecycle;
</script>

<style lang="scss" scoped>
.request-lifecycle {
    container-type: inline-size;
    min-width: 0;
    padding: 8px 12px;
    border: 1px solid #e4eaef;
    border-radius: 8px;
    color: #484848;
    font-size: 0.875rem;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 7px;
    }

    &__heading {
        color: #707e86;
        font-size: 0.75rem;
        font-weight: 500;
    }

    &__toggle {
        padding: 0;
        border: 0;
        background: transparent;
        color: #168a59;
        font: inherit;
        font-size: 0.75rem;
        cursor: pointer;

        &:focus-visible {
            outline: 2px solid #168a59;
            outline-offset: 3px;
        }
    }

    &__chevron {
        display: inline-block;
        width: 12px;
        text-align: center;
    }

    &__steps {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    &__step {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        min-width: 0;
        color: #8b969d;
        text-align: center;

        &:not(:last-child)::after {
            position: absolute;
            top: 7px;
            left: calc(50% + 10px);
            width: calc(100% - 20px);
            height: 1px;
            background: #dce3e7;
            content: '';
        }

        &_link-done:not(:last-child)::after {
            background: #168a59;
        }

        &_done,
        &_active {
            color: #168a59;
        }

        &_active {
            font-weight: 600;
        }
    }

    &__marker {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 15px;
        height: 15px;
        border: 1px solid currentColor;
        border-radius: 50%;
        background: #fff;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
    }

    &__step_done &__marker {
        border-color: #168a59;
        background: #168a59;
        color: #fff;
    }

    &__step_active &__marker {
        border-width: 2px;
        box-shadow: inset 0 0 0 2px #fff;
        background: #168a59;
    }

    &__label {
        max-width: 100%;
        line-height: 1.2;
        overflow-wrap: break-word;
    }

    &__summary {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 0 4px;
        margin-top: 7px;
        padding-top: 6px;
        border-top: 1px solid #edf0f2;
        color: #596970;
        font-size: 0.75rem;
        line-height: 1.3;
    }

    &__summary-icon {
        align-self: center;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 13px;
        width: 13px;
        height: 13px;
        margin-right: 2px;
        border: 1px solid #8b969d;
        border-radius: 50%;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
    }

    &__details {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #e4eaef;
    }

    &__current {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 16px;
    }

    &__events {
        margin: 8px 0 0;
        padding-left: 18px;

        li {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 4px 16px;
            padding: 3px 0;
        }

        time {
            color: #8b969d;
        }
    }

    &__unknown {
        margin: 8px 0 0;
        color: #8b969d;
    }
}

@container (max-width: 360px) {
    .request-lifecycle__label {
        font-size: 0.75rem;
    }
}
</style>
